import { WidgetType } from "@codemirror/view";
import { attachSyntaxEditInteractions } from "../shared/widgets";
import type {
    MarkdownRichBlockKind,
    MarkdownRichBlockRange,
    MarkdownRichInlineKind,
} from "./scan";
import { findMarkdownRichInlineMatches } from "./scan";

export class PrettyMarkdownInlineRichWidget extends WidgetType {
    constructor(
        readonly kind: MarkdownRichInlineKind,
        readonly text: string,
        readonly editPosition: number,
        readonly id: string | null = null,
    ) {
        super();
    }

    eq(other: PrettyMarkdownInlineRichWidget): boolean {
        return other.kind === this.kind &&
            other.text === this.text &&
            other.editPosition === this.editPosition &&
            other.id === this.id;
    }

    toDOM(): HTMLElement {
        const wrapper = document.createElement("span");
        wrapper.className = [
            "cm-magic-markdown-rich-inline",
            `cm-magic-markdown-rich-inline-${this.kind}`,
        ].join(" ");
        wrapper.title = this.kind === "footnote-ref"
            ? `Footnote ${this.id ?? this.text}`
            : this.text;

        if (this.kind === "footnote-ref") {
            const sup = document.createElement("sup");
            sup.className = "cm-magic-markdown-rich-footnote-ref";
            sup.textContent = this.id ?? this.text;
            wrapper.appendChild(sup);
        } else if (this.kind === "code") {
            const code = document.createElement("code");
            code.className = "cm-magic-markdown-rich-code";
            code.textContent = this.text;
            wrapper.appendChild(code);
        } else {
            wrapper.textContent = this.text;
        }

        attachSyntaxEditInteractions(wrapper, {
            keyboard: false,
            position: this.editPosition,
        });
        return wrapper;
    }

    ignoreEvent(): boolean {
        return true;
    }
}

export class PrettyMarkdownRichBlockWidget extends WidgetType {
    constructor(
        readonly range: MarkdownRichBlockRange,
        readonly editPosition: number,
    ) {
        super();
    }

    eq(other: PrettyMarkdownRichBlockWidget): boolean {
        return other.range.kind === this.range.kind &&
            other.range.raw === this.range.raw &&
            other.editPosition === this.editPosition;
    }

    toDOM(): HTMLElement {
        const wrapper = document.createElement("div");
        wrapper.className = [
            "cm-magic-markdown-rich-block",
            `cm-magic-markdown-rich-block-${this.range.kind}`,
        ].join(" ");
        wrapper.title = this.range.raw;

        wrapper.appendChild(createBlockBody(this.range));

        attachSyntaxEditInteractions(wrapper, {
            keyboard: false,
            position: this.editPosition,
        });
        return wrapper;
    }

    ignoreEvent(): boolean {
        return true;
    }
}

function createBlockBody(range: MarkdownRichBlockRange): HTMLElement {
    if (range.kind === "table" && range.table) {
        return createTablePreview(range);
    }
    if (range.kind === "frontmatter") {
        return createFrontmatterPreview(range);
    }
    if (range.kind === "definition-list") {
        return createDefinitionListPreview(range);
    }
    if (range.kind === "blockquote") {
        return createBlockquotePreview(range);
    }
    if (range.kind === "footnote-definition") {
        return createFootnoteDefinitionPreview(range);
    }

    const body = document.createElement("span");
    body.className = "cm-magic-markdown-rich-block-body";

    const icon = createBlockIcon(range.kind);
    if (icon) body.appendChild(icon);

    const title = document.createElement("span");
    title.className = "cm-magic-markdown-rich-block-title";
    title.textContent = getBlockTitle(range);
    body.appendChild(title);

    const detailText = getBlockDetail(range);
    if (detailText) {
        const detail = document.createElement("span");
        detail.className = "cm-magic-markdown-rich-block-detail";
        detail.textContent = detailText;
        body.appendChild(detail);
    }

    return body;
}

function getBlockTitle(range: MarkdownRichBlockRange): string {
    switch (range.kind) {
        case "frontmatter":
            return "Frontmatter";
        case "definition-list":
            return range.title;
        case "blockquote":
            return "Quote";
        case "footnote-definition":
            return `Footnote ${range.title}`;
        case "table":
            return "Table";
    }
}

function getBlockDetail(range: MarkdownRichBlockRange): string {
    switch (range.kind) {
        case "frontmatter":
            return "";
        case "definition-list":
            return range.body;
        case "blockquote":
            return range.body;
        case "footnote-definition":
            return range.body;
        case "table":
            return "";
    }
}

function createFrontmatterPreview(range: MarkdownRichBlockRange): HTMLElement {
    const body = document.createElement("span");
    body.className = "cm-magic-markdown-rich-block-body cm-magic-markdown-rich-frontmatter-body";

    const icon = createBlockIcon(range.kind);
    if (icon) body.appendChild(icon);

    const title = document.createElement("span");
    title.className = "cm-magic-markdown-rich-block-title cm-magic-markdown-rich-frontmatter-title";
    title.textContent = "Frontmatter";
    body.appendChild(title);

    return body;
}

function createDefinitionListPreview(range: MarkdownRichBlockRange): HTMLElement {
    const dl = document.createElement("dl");
    dl.className = "cm-magic-markdown-rich-definition-list";

    const dt = document.createElement("dt");
    dt.className = "cm-magic-markdown-rich-definition-term";
    dt.textContent = range.title;
    dl.appendChild(dt);

    const definitions = range.items?.length ? range.items : [range.body];
    for (const definition of definitions) {
        if (!definition) continue;
        const dd = document.createElement("dd");
        dd.className = "cm-magic-markdown-rich-definition-body";
        dd.textContent = definition;
        dl.appendChild(dd);
    }

    return dl;
}

function createBlockquotePreview(range: MarkdownRichBlockRange): HTMLElement {
    const quote = document.createElement("blockquote");
    quote.className = "cm-magic-markdown-rich-blockquote";
    const lines = range.items?.length ? range.items : [range.body];
    const paragraph = document.createElement("p");
    paragraph.className = "cm-magic-markdown-rich-blockquote-line";
    lines.forEach((line, index) => {
        if (index > 0) paragraph.appendChild(document.createElement("br"));
        appendMarkdownRichInlineText(paragraph, line);
    });
    quote.appendChild(paragraph);
    return quote;
}

function appendMarkdownRichInlineText(parent: HTMLElement, text: string): void {
    const matches = findMarkdownRichInlineMatches(text);
    let cursor = 0;
    for (const match of matches) {
        if (match.fromIndex < cursor) continue;
        if (match.fromIndex > cursor) {
            parent.appendChild(document.createTextNode(text.slice(cursor, match.fromIndex)));
        }
        if (match.kind === "footnote-ref") {
            const sup = document.createElement("sup");
            sup.className = "cm-magic-markdown-rich-footnote-ref";
            sup.textContent = match.id ?? match.text;
            parent.appendChild(sup);
        } else if (match.kind === "code") {
            const code = document.createElement("code");
            code.className = "cm-magic-markdown-rich-code cm-magic-markdown-rich-text-code";
            code.textContent = match.text;
            parent.appendChild(code);
        } else {
            const span = document.createElement("span");
            span.className = [
                "cm-magic-markdown-rich-text",
                `cm-magic-markdown-rich-text-${match.kind}`,
            ].join(" ");
            span.textContent = match.text;
            parent.appendChild(span);
        }
        cursor = match.toIndex;
    }
    if (cursor < text.length) {
        parent.appendChild(document.createTextNode(text.slice(cursor)));
    }
}

function createFootnoteDefinitionPreview(range: MarkdownRichBlockRange): HTMLElement {
    const body = document.createElement("span");
    body.className = [
        "cm-magic-markdown-rich-block-body",
        "cm-magic-markdown-rich-footnote-definition-body",
    ].join(" ");

    const icon = createBlockIcon(range.kind);
    if (icon) body.appendChild(icon);

    const label = document.createElement("span");
    label.className = "cm-magic-markdown-rich-footnote-definition-label";
    label.textContent = `${range.title}:`;
    body.appendChild(label);

    if (range.body) {
        const text = document.createElement("span");
        text.className = "cm-magic-markdown-rich-footnote-definition-text";
        appendMarkdownRichInlineText(text, range.body);
        body.appendChild(text);
    }

    return body;
}

function createTablePreview(range: MarkdownRichBlockRange): HTMLElement {
    const body = document.createElement("span");
    body.className = "cm-magic-markdown-rich-block-body cm-magic-markdown-rich-table-body";

    const table = document.createElement("table");
    table.className = "cm-magic-markdown-rich-table";

    const thead = document.createElement("thead");
    const headerRow = document.createElement("tr");
    for (const [index, header] of (range.table?.headers ?? []).entries()) {
        const th = document.createElement("th");
        th.textContent = header;
        setCellAlignment(th, range.table?.alignments[index] ?? null);
        headerRow.appendChild(th);
    }
    thead.appendChild(headerRow);
    table.appendChild(thead);

    const tbody = document.createElement("tbody");
    for (const row of range.table?.rows ?? []) {
        const tr = document.createElement("tr");
        row.forEach((cell, index) => {
            const td = document.createElement("td");
            td.textContent = cell;
            setCellAlignment(td, range.table?.alignments[index] ?? null);
            tr.appendChild(td);
        });
        tbody.appendChild(tr);
    }
    table.appendChild(tbody);
    body.appendChild(table);

    return body;
}

function setCellAlignment(
    cell: HTMLTableCellElement,
    alignment: "left" | "center" | "right" | null,
): void {
    if (alignment) cell.style.textAlign = alignment;
}

function createBlockIcon(kind: MarkdownRichBlockKind): SVGElement | null {
    if (kind === "definition-list" || kind === "table") return null;

    const ns = "http://www.w3.org/2000/svg";
    const svg = document.createElementNS(ns, "svg");
    svg.classList.add("cm-magic-markdown-rich-icon");
    svg.setAttribute("viewBox", "0 0 24 24");
    svg.setAttribute("fill", "none");
    svg.setAttribute("stroke", "currentColor");
    svg.setAttribute("stroke-width", "2");
    svg.setAttribute("stroke-linecap", "round");
    svg.setAttribute("stroke-linejoin", "round");

    const append = (tag: string, attrs: Record<string, string>) => {
        const el = document.createElementNS(ns, tag);
        for (const [name, value] of Object.entries(attrs)) {
            el.setAttribute(name, value);
        }
        svg.appendChild(el);
    };

    switch (kind) {
        case "frontmatter":
            append("path", { d: "M6 5h12" });
            append("path", { d: "M8 9h8" });
            append("path", { d: "M8 13h5" });
            append("path", { d: "M6 19h12" });
            append("path", { d: "M6 5v14M18 5v14" });
            return svg;
        case "blockquote":
            append("path", { d: "M8 7h4v5H9v3h3v2H7v-5h3V9H8z" });
            append("path", { d: "M16 7h4v5h-3v3h3v2h-5v-5h3V9h-2z" });
            return svg;
        case "footnote-definition":
            append("path", { d: "M6 18h12M8 15l4-10 4 10M10 11h4" });
            append("path", { d: "M18 5h2M19 4v2" });
            return svg;
    }
}
